import { Injectable, inject } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';

import type { PaginationMeta } from '@core/http-types';
import { createEffectFactory } from '@core/ngrx-legacy';
import { JobBasic, OfferKind } from '@core/offers-types';

import { JobsHttpService } from '../jobs-http.service';
import { JobsBasicActions } from './jobs-basic.store';

@Injectable({
  providedIn: 'root',
})
export class JobsBasicEffects {
  private readonly jobsHttpService = inject(JobsHttpService);
  private readonly actions$ = inject(Actions);

  public effects = createEffectFactory(JobsBasicActions, this.actions$);

  public fetchCollection$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsBasicActions.fetchCollection),
      switchMap(() =>
        this.fetchBasicCollection([], 1).pipe(
          map(response => response.offers),
          switchMap(jobs => [
            JobsBasicActions.fetchCollectionSuccess({
              payload: { data: jobs.map((job: JobBasic) => ({ ...job, kind: OfferKind.Job })) },
            }),
          ]),
          catchError(error =>
            of(
              JobsBasicActions.fetchCollectionFailed({
                payload: { error },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public remove$ = createEffect(() =>
    this.actions$.pipe(
      ofType(JobsBasicActions.removeOnePessimistic),
      mergeMap(({ payload }) =>
        this.jobsHttpService.delete$({ id: <number>payload.data }).pipe(
          switchMap(response => [
            JobsBasicActions.removeOnePessimisticSuccess({
              payload: { data: response.offer },
            }),
          ]),
          catchError(error =>
            of(
              JobsBasicActions.removeOnePessimisticFailed({
                payload: { error, data: payload.data },
              }),
            ),
          ),
        ),
      ),
    ),
  );

  public updatePositions$ = this.effects(JobsBasicActions.updatePositions, ({ payload }) =>
    this.jobsHttpService.updatePositions$({ positions: payload }).pipe(
      switchMap(response => of(JobsBasicActions.updatePositionSuccess({ payload: response }))),
      catchError(error => of(JobsBasicActions.updatePositionsFailed({ payload: { error } }))),
    ),
  );

  public switchJobsToProsemirror$ = this.effects(JobsBasicActions.switchJobsToProsemirror, () =>
    this.jobsHttpService.switchJobsToProsemirror$().pipe(
      switchMap(response => [
        JobsBasicActions.switchJobsToProsemirrorSuccess({ payload: response }),
      ]),
      catchError(error => [JobsBasicActions.switchJobsToProsemirrorFailed({ payload: { error } })]),
    ),
  );

  private fetchBasicCollection(
    offers: JobBasic[],
    page: number,
  ): Observable<{ offers: JobBasic[]; meta: PaginationMeta }> {
    return this.jobsHttpService.fetchBasicCollection$(page).pipe(
      switchMap(response => {
        const fetchedAll = response.meta.limit * response.meta.page > response.meta.totalCount;
        return fetchedAll
          ? of<{ offers: JobBasic[]; meta: PaginationMeta }>({
              ...response,
              offers: [...offers, ...response.offers],
            })
          : this.fetchBasicCollection([...offers, ...response.offers], response.meta.page + 1);
      }),
    );
  }
}
